import { useCallback } from "react";
import { z } from "zod";

import type { ExchangeTemplateFilters } from "@/components/NewOptinDialog/templateMetadata";

import {
  EXCHANGE_TYPES,
  TEMPLATE_MEDIUMS,
  STATUS_TYPES,
} from "@/components/NewOptinDialog/templateMetadata";

export type ExchangesFilters = ExchangeTemplateFilters;

export const ExchangesFiltersSchema = z
  .object({
    medium: z.enum(TEMPLATE_MEDIUMS).optional().catch(undefined),
    exchangeType: z.enum(EXCHANGE_TYPES).optional().catch(undefined),
    status: z.enum(STATUS_TYPES).optional().catch(undefined),
    search: z.string().optional().catch(undefined),
  })
  .transform(
    (search): ExchangesFilters => ({
      medium: search.medium,
      exchangeType: search.exchangeType,
      status: search.status,
      search: search.search,
    }),
  );

interface NavigateOptions {
  search: (prev: ExchangesFilters) => ExchangesFilters;
  replace?: boolean;
}

interface FilterRoute {
  useSearch: () => ExchangesFilters;
  useNavigate: () => (opts: NavigateOptions) => unknown;
}

/**
 * Reads a route's search params and returns a stable `updateFilter(key,
 * value)` callback that patches a single key of those search params,
 * removing the key entirely when `value` is `undefined`.
 *
 * Every exchanges route shares the same `ExchangesFilters` search shape, so
 * this isn't generic over the route's search type — pass any TanStack
 * Router file route (the `Route` export of a route file) whose
 * `validateSearch` produces `ExchangesFilters`.
 */
export function useUpdateExchangesFilter(route: FilterRoute) {
  const filters = route.useSearch();
  const navigate = route.useNavigate();

  const updateFilter = useCallback(
    <K extends keyof ExchangesFilters>(
      key: K,
      value: ExchangesFilters[K] | undefined,
    ) => {
      void navigate({
        search: (prev): ExchangesFilters => {
          const next = { ...prev };
          if (value === undefined) {
            delete next[key];
          } else {
            next[key] = value;
          }
          return next;
        },
        replace: true,
      });
    },
    [navigate],
  );

  return { filters, updateFilter };
}

export type UpdateExchangesFilter = ReturnType<
  typeof useUpdateExchangesFilter
>["updateFilter"];
